import { Link } from 'react-router-dom';
import { TOOL_SEO_COPY } from '../../data/toolSeoCopy';
import { getToolBySlug } from '../../data/toolRegistry';

interface ToolSeoBlockProps {
  toolSlug: string;
  className?: string;
}

/**
 * Cross-linking clusters for internal link equity.
 * Each key maps a tool slug to an array of related tool slugs with labels.
 * These create contextual internal links that boost topical authority.
 */
const RELATED_TOOLS: Record<string, Array<{ slug: string; path: string; label: string }>> = {
  // ── PDF Suite cluster ─────────────────────
  'merge-pdf': [
    { slug: 'split-pdf', path: '/micro-tools/split-pdf', label: 'Split PDF' },
    { slug: 'protect-pdf', path: '/micro-tools/protect-pdf', label: 'Password Protect PDF' },
    { slug: 'freelance-pdf-e-sign-tool', path: '/sign', label: 'E-Sign PDF' },
  ],
  'split-pdf': [
    { slug: 'merge-pdf', path: '/micro-tools/merge-pdf', label: 'Merge PDF' },
    { slug: 'protect-pdf', path: '/micro-tools/protect-pdf', label: 'Password Protect PDF' },
    { slug: 'freelance-pdf-e-sign-tool', path: '/sign', label: 'E-Sign PDF' },
  ],
  'protect-pdf': [
    { slug: 'merge-pdf', path: '/micro-tools/merge-pdf', label: 'Merge PDF' },
    { slug: 'split-pdf', path: '/micro-tools/split-pdf', label: 'Split PDF' },
    { slug: 'freelance-pdf-e-sign-tool', path: '/sign', label: 'E-Sign PDF' },
  ],

  // ── Image Suite cluster ───────────────────
  'webp-to-png-jpg': [
    { slug: 'png-to-jpg', path: '/micro-tools/png-to-jpg', label: 'PNG to JPG' },
    { slug: 'jpg-to-png', path: '/micro-tools/jpg-to-png', label: 'JPG to PNG' },
    { slug: 'image-compressor', path: '/micro-tools/image-compressor', label: 'Image Compressor' },
  ],
  'png-to-jpg': [
    { slug: 'jpg-to-png', path: '/micro-tools/jpg-to-png', label: 'JPG to PNG' },
    { slug: 'webp-to-png-jpg', path: '/micro-tools/webp-to-png-jpg', label: 'WebP to PNG/JPG' },
    { slug: 'image-compressor', path: '/micro-tools/image-compressor', label: 'Image Compressor' },
  ],
  'jpg-to-png': [
    { slug: 'png-to-jpg', path: '/micro-tools/png-to-jpg', label: 'PNG to JPG' },
    { slug: 'webp-to-png-jpg', path: '/micro-tools/webp-to-png-jpg', label: 'WebP to PNG/JPG' },
    { slug: 'image-compressor', path: '/micro-tools/image-compressor', label: 'Image Compressor' },
  ],
  'image-compressor': [
    { slug: 'webp-to-png-jpg', path: '/micro-tools/webp-to-png-jpg', label: 'WebP to PNG/JPG' },
    { slug: 'png-to-jpg', path: '/micro-tools/png-to-jpg', label: 'PNG to JPG' },
    { slug: 'favicon-generator', path: '/micro-tools/favicon-generator', label: 'Favicon Generator' },
  ],
  'favicon-generator': [
    { slug: 'image-compressor', path: '/micro-tools/image-compressor', label: 'Image Compressor' },
    { slug: 'social-header-resizer', path: '/micro-tools/social-header-resizer', label: 'Social Header Resizer' },
    { slug: 'webp-to-png-jpg', path: '/micro-tools/webp-to-png-jpg', label: 'WebP to PNG/JPG' },
  ],
  'social-header-resizer': [
    { slug: 'favicon-generator', path: '/micro-tools/favicon-generator', label: 'Favicon Generator' },
    { slug: 'image-compressor', path: '/micro-tools/image-compressor', label: 'Image Compressor' },
    { slug: 'png-to-jpg', path: '/micro-tools/png-to-jpg', label: 'PNG to JPG' },
  ],

  // ── Document Generator cluster ────────────
  'upi-invoice-generator-india': [
    { slug: 'protect-pdf', path: '/micro-tools/protect-pdf', label: 'Password Protect PDF' },
    { slug: 'freelance-scope-of-work-generator', path: '/tools/freelance-scope-of-work-generator', label: 'Scope of Work Generator' },
    { slug: 'late-fee-calculator', path: '/micro-tools/late-fee-calculator', label: 'Late Fee Calculator' },
  ],
  'freelance-proposal-generator': [
    { slug: 'freelance-scope-of-work-generator', path: '/tools/freelance-scope-of-work-generator', label: 'Scope of Work Generator' },
    { slug: 'freelance-nda-generator', path: '/tools/freelance-nda-generator', label: 'NDA Generator' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
  ],
  'freelance-scope-of-work-generator': [
    { slug: 'freelance-proposal-generator', path: '/tools/freelance-proposal-generator', label: 'Proposal Generator' },
    { slug: 'freelance-nda-generator', path: '/tools/freelance-nda-generator', label: 'NDA Generator' },
    { slug: 'freelance-sign-off-certificate-generator', path: '/tools/freelance-sign-off-certificate-generator', label: 'Sign-Off Certificate' },
  ],
  'freelance-nda-generator': [
    { slug: 'freelance-scope-of-work-generator', path: '/tools/freelance-scope-of-work-generator', label: 'Scope of Work Generator' },
    { slug: 'protect-pdf', path: '/micro-tools/protect-pdf', label: 'Password Protect PDF' },
    { slug: 'freelance-pdf-e-sign-tool', path: '/sign', label: 'E-Sign PDF' },
  ],
  'freelance-legal-notice-generator-india': [
    { slug: 'freelance-payment-escalation-emails', path: '/tools/freelance-payment-escalation-emails', label: 'Payment Escalation Emails' },
    { slug: 'late-fee-calculator', path: '/micro-tools/late-fee-calculator', label: 'Late Fee Calculator' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
  ],
  'freelance-payment-escalation-emails': [
    { slug: 'freelance-legal-notice-generator-india', path: '/tools/freelance-legal-notice-generator-india', label: 'Legal Notice Generator' },
    { slug: 'late-fee-calculator', path: '/micro-tools/late-fee-calculator', label: 'Late Fee Calculator' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
  ],
  'freelance-sign-off-certificate-generator': [
    { slug: 'freelance-ip-release-testimonial-form', path: '/tools/freelance-ip-release-testimonial-form', label: 'IP Release & Testimonial' },
    { slug: 'freelance-scope-of-work-generator', path: '/tools/freelance-scope-of-work-generator', label: 'Scope of Work Generator' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
  ],
  'freelance-ip-release-testimonial-form': [
    { slug: 'freelance-sign-off-certificate-generator', path: '/tools/freelance-sign-off-certificate-generator', label: 'Sign-Off Certificate' },
    { slug: 'draft-watermarker', path: '/tools/draft-watermarker', label: 'Draft Watermarker' },
    { slug: 'freelance-pdf-e-sign-tool', path: '/sign', label: 'E-Sign PDF' },
  ],
  'draft-watermarker': [
    { slug: 'protect-pdf', path: '/micro-tools/protect-pdf', label: 'Password Protect PDF' },
    { slug: 'freelance-nda-generator', path: '/tools/freelance-nda-generator', label: 'NDA Generator' },
    { slug: 'image-compressor', path: '/micro-tools/image-compressor', label: 'Image Compressor' },
  ],
  'interactive-html-proposal-builder': [
    { slug: 'freelance-proposal-generator', path: '/tools/freelance-proposal-generator', label: 'PDF Proposal Generator' },
    { slug: 'freelance-scope-of-work-generator', path: '/tools/freelance-scope-of-work-generator', label: 'Scope of Work Generator' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
  ],
  'freelance-pdf-e-sign-tool': [
    { slug: 'protect-pdf', path: '/micro-tools/protect-pdf', label: 'Password Protect PDF' },
    { slug: 'merge-pdf', path: '/micro-tools/merge-pdf', label: 'Merge PDF' },
    { slug: 'freelance-nda-generator', path: '/tools/freelance-nda-generator', label: 'NDA Generator' },
  ],

  // ── Calculator cluster ────────────────────
  'hourly-rate-calculator': [
    { slug: 'late-fee-calculator', path: '/micro-tools/late-fee-calculator', label: 'Late Fee Calculator' },
    { slug: 'tds-calculator', path: '/micro-tools/tds-calculator', label: 'TDS Calculator' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
  ],
  'late-fee-calculator': [
    { slug: 'hourly-rate-calculator', path: '/micro-tools/hourly-rate-calculator', label: 'Hourly Rate Calculator' },
    { slug: 'freelance-payment-escalation-emails', path: '/tools/freelance-payment-escalation-emails', label: 'Payment Escalation Emails' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
  ],
  'tds-calculator': [
    { slug: 'hourly-rate-calculator', path: '/micro-tools/hourly-rate-calculator', label: 'Hourly Rate Calculator' },
    { slug: 'upi-invoice-generator-india', path: '/tools/upi-invoice-generator-india', label: 'Invoice Generator' },
    { slug: 'late-fee-calculator', path: '/micro-tools/late-fee-calculator', label: 'Late Fee Calculator' },
  ],

  // ── Developer / Writer cluster ────────────
  'json-formatter': [
    { slug: 'base64-encoder-decoder', path: '/micro-tools/base64-encoder-decoder', label: 'Base64 Encoder/Decoder' },
    { slug: 'markdown-to-html', path: '/micro-tools/markdown-to-html', label: 'Markdown to HTML' },
    { slug: 'file-hash-checker', path: '/micro-tools/file-hash-checker', label: 'File Hash Checker' },
  ],
  'markdown-to-html': [
    { slug: 'word-character-counter', path: '/micro-tools/word-character-counter', label: 'Word & Character Counter' },
    { slug: 'json-formatter', path: '/micro-tools/json-formatter', label: 'JSON Formatter' },
    { slug: 'font-changer', path: '/micro-tools/font-changer', label: 'Fancy Font Generator' },
  ],
  'word-character-counter': [
    { slug: 'markdown-to-html', path: '/micro-tools/markdown-to-html', label: 'Markdown to HTML' },
    { slug: 'font-changer', path: '/micro-tools/font-changer', label: 'Fancy Font Generator' },
    { slug: 'json-formatter', path: '/micro-tools/json-formatter', label: 'JSON Formatter' },
  ],
  'base64-encoder-decoder': [
    { slug: 'json-formatter', path: '/micro-tools/json-formatter', label: 'JSON Formatter' },
    { slug: 'file-hash-checker', path: '/micro-tools/file-hash-checker', label: 'File Hash Checker' },
    { slug: 'markdown-to-html', path: '/micro-tools/markdown-to-html', label: 'Markdown to HTML' },
  ],
  'file-hash-checker': [
    { slug: 'protect-pdf', path: '/micro-tools/protect-pdf', label: 'Password Protect PDF' },
    { slug: 'base64-encoder-decoder', path: '/micro-tools/base64-encoder-decoder', label: 'Base64 Encoder/Decoder' },
    { slug: 'json-formatter', path: '/micro-tools/json-formatter', label: 'JSON Formatter' },
  ],
  'font-changer': [
    { slug: 'word-character-counter', path: '/micro-tools/word-character-counter', label: 'Word & Character Counter' },
    { slug: 'social-header-resizer', path: '/micro-tools/social-header-resizer', label: 'Social Header Resizer' },
    { slug: 'markdown-to-html', path: '/micro-tools/markdown-to-html', label: 'Markdown to HTML' },
  ],
};

export function ToolSeoBlock({ toolSlug, className = '' }: ToolSeoBlockProps) {
  const copy = TOOL_SEO_COPY[toolSlug];

  if (!copy) {
    return null;
  }

  const introParagraphs = copy.intro.split('\n\n').filter(Boolean);
  const relatedTools = RELATED_TOOLS[toolSlug] || [];

  return (
    <section className={`mx-auto w-full max-w-[980px] px-6 pb-16 pt-10 ${className}`}>
      <div className="rounded-2xl border border-stone-200/70 bg-white/90 p-6 md:p-8 shadow-sm">
        <h2 className="text-xl md:text-2xl font-bold text-stone-900 tracking-tight mb-3">
          {copy.headline}
        </h2>
        <div className="space-y-4 mb-6">
          {introParagraphs.map((paragraph, index) => (
            <p key={index} className="text-[15px] text-stone-600 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        <h3 className="text-[15px] font-bold text-stone-900 uppercase tracking-[0.12em] mb-3">
          How This Local-First Tool Works
        </h3>
        <ul className="space-y-2 text-[14px] text-stone-600 leading-relaxed mb-6 list-disc list-inside">
          {copy.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>

        <h3 className="text-[15px] font-bold text-stone-900 uppercase tracking-[0.12em] mb-3">
          Frequently Asked Questions
        </h3>
        {/* Renders every FAQ as a real question/answer pair. It previously
            hardcoded faqs[0] and faqs[1] into one paragraph each, prefixed
            "FAQ 1:" — so any further answers were written but never shown, and
            the two that were shown had no heading a crawler could read. */}
        <dl className="space-y-4">
          {copy.faqs.map((faq) => (
            <div key={faq.question}>
              <dt className="text-[14.5px] font-semibold text-stone-900 leading-snug">
                {faq.question}
              </dt>
              <dd className="mt-1.5 text-[14px] text-stone-600 leading-relaxed">{faq.answer}</dd>
            </div>
          ))}
        </dl>

        {/* Cross-linking section */}
        {relatedTools.length > 0 && (
          <div className="mt-6 pt-5 border-t border-stone-200/70">
            <h3 className="text-[13px] font-bold text-stone-900 uppercase tracking-[0.12em] mb-3">
              Related Free Tools
            </h3>
            <div className="flex flex-wrap gap-2">
              {relatedTools.map((tool) => (
                <Link
                  key={tool.slug}
                  // Resolve from the registry so these cross-links follow the
                  // canonical URL automatically; the hardcoded path is only a
                  // fallback for a slug that is not in the registry.
                  to={getToolBySlug(tool.slug)?.canonicalPath ?? tool.path}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-stone-50 border border-stone-200/80 text-[13px] font-medium text-stone-700 hover:bg-stone-100 hover:border-stone-300 hover:text-stone-900 transition-all"
                >
                  {tool.label}
                  <span className="text-stone-400">→</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <p className="mt-6 text-[14px] text-stone-700 leading-relaxed">
          {copy.cta}
        </p>
      </div>
    </section>
  );
}
